"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useForm = void 0;
const react_1 = require("react");
const flatArray_1 = require("../../utils/flatArray");
const object_1 = require("../../utils/object");
const descriptors_1 = require("./descriptors");
const validation_1 = require("./validation");
const sameModifiers = (x, y) => {
    if (x === y)
        return true;
    if (x === undefined)
        return false;
    if (y === undefined)
        return false;
    if (x.length !== y.length)
        return false;
    const length = x.length;
    for (let i = 0; i < length; i++) {
        if (x[i] !== y[i])
            return false;
    }
    return true;
};
const useModifiers = (descriptor, config) => {
    const ref = (0, react_1.useRef)({
        descriptor,
        config,
        modifiers: {},
    });
    ref.current.descriptor = descriptor;
    ref.current.config = config;
    const getModifiers = (0, react_1.useCallback)((key, values) => {
        var _a, _b;
        const d = ref.current.descriptor[key];
        const c = ref.current.config[key];
        const modifiers = (0, flatArray_1.flatArray)([
            (((_a = d.restrictions) === null || _a === void 0 ? void 0 : _a.required) || ((_b = c === null || c === void 0 ? void 0 : c.restrictions) === null || _b === void 0 ? void 0 : _b.required)) ? 'required' : undefined,
            c === null || c === void 0 ? void 0 : c.modifiers,
        ]);
        const prevModifiers = ref.current.modifiers[key];
        if (sameModifiers(modifiers, prevModifiers))
            return prevModifiers;
        ref.current.modifiers[key] = modifiers;
        return modifiers;
    }, []);
    return getModifiers;
};
const useValues = (options) => {
    const { descriptor, config, propsValues, onChange, validateForm } = options;
    const [, rerender] = (0, react_1.useState)({});
    const ref = (0, react_1.useRef)({});
    const state = ref.current;
    state.onChange = onChange;
    state.validateForm = validateForm;
    state.config = config !== null && config !== void 0 ? config : {};
    // It is advisable to never change the descriptor.
    // Descriptor is Design Time
    if (state.descriptor != descriptor) {
        state.descriptor = descriptor;
        state.propsValues = {};
        state.inputs = {};
        state.searchInputs = {};
        state.values = {};
        state.errors = {};
        state.touched = {};
        (0, descriptors_1.mapDescriptor)(descriptor, ([key, d]) => {
            const value = undefined;
            const input = d.toInput(value);
            state.inputs[key] = input;
            state.values[key] = value;
        });
        state.validateAll = (_, final = true) => {
            var _a;
            const formErrors = final ? (0, validation_1.parseFormErrors)((_a = state.validateForm) === null || _a === void 0 ? void 0 : _a.call(state, state.values)) : undefined;
            (0, descriptors_1.mapDescriptor)(descriptor, ([key, d]) => {
                var _a, _b, _c, _d, _e;
                const c = state.config[key];
                const input = state.inputs[key];
                const value = state.values[key];
                if (final)
                    state.touched[key] = true;
                const touched = (_a = state.touched[key]) !== null && _a !== void 0 ? _a : false;
                state.errors[key] = (c === null || c === void 0 ? void 0 : c.skipValidation) ? undefined : (0, validation_1.pipeFieldErrors)((_b = d.validateInput) === null || _b === void 0 ? void 0 : _b.call(d, input), (_c = d.validateValue) === null || _c === void 0 ? void 0 : _c.call(d, value), (0, validation_1.validateByRestrictions)(value, touched, d.restrictions), (0, validation_1.validateByRestrictions)(value, touched, c === null || c === void 0 ? void 0 : c.restrictions), (_d = d.validateByValues) === null || _d === void 0 ? void 0 : _d.call(d, state.values), (_e = c === null || c === void 0 ? void 0 : c.validateByValues) === null || _e === void 0 ? void 0 : _e.call(c, state.values), formErrors === null || formErrors === void 0 ? void 0 : formErrors[key]);
            });
            if (final)
                rerender({});
            return !state.hasErrors();
        };
        const setInputAndValue = (key, input, value) => {
            var _a;
            state.inputs[key] = input;
            state.values[key] = value;
            state.touched[key] = true;
            state.searchInputs[key] = undefined;
            state.validateAll(state.values, false);
            (_a = state.onChange) === null || _a === void 0 ? void 0 : _a.call(state, {
                ...state.values,
            });
            rerender(x => !x);
        };
        state.setInput = (key, input) => {
            const d = descriptor[key];
            const value = d.toValue(input);
            setInputAndValue(key, input, value);
        };
        state.setValue = (key, value) => {
            const d = descriptor[key];
            const input = d.toInput(value);
            setInputAndValue(key, input, value);
        };
        state.setSearch = (key, input) => {
            var _a, _b;
            state.searchInputs[key] = input !== null && input !== void 0 ? input : '';
            (_b = (_a = state.config[key]) === null || _a === void 0 ? void 0 : _a.search) === null || _b === void 0 ? void 0 : _b.call(_a, input);
            rerender(x => !x);
        };
        state.hasErrors = () => {
            const rec = (0, descriptors_1.mapDescriptor)(descriptor, ([key]) => { var _a; return (_a = state.errors[key]) === null || _a === void 0 ? void 0 : _a.length; });
            const count = (0, object_1.getObjectEntries)(rec).filter(([, x]) => x).length;
            return Boolean(count);
        };
    }
    // handle propsValues onChange.
    // Reset form field state if the field props value has changed.
    let needRevalidate = false;
    (0, descriptors_1.mapDescriptor)(descriptor, ([key, d]) => {
        const value = propsValues === null || propsValues === void 0 ? void 0 : propsValues[key];
        if (state.propsValues[key] !== value && state.values[key] !== value) {
            const input = d.toInput(value);
            state.inputs[key] = input;
            state.values[key] = value;
            state.searchInputs[key] = undefined;
            needRevalidate = true;
        }
        state.propsValues[key] = value;
    });
    if (needRevalidate) {
        state.validateAll(state.values, false);
    }
    return state;
};
/** @deprecated Use useValueForm */
const useForm = (options) => {
    var _a;
    const { descriptor } = options;
    const config = (_a = options.config) !== null && _a !== void 0 ? _a : {};
    const { inputs, values, searchInputs, setInput, setValue, setSearch, validateAll, errors, hasErrors, } = useValues(options);
    const getModifiers = useModifiers(descriptor, config);
    const onEvents = (0, react_1.useMemo)(() => ((0, descriptors_1.mapDescriptor)(descriptor, ([key, d]) => (...args) => {
        const input = d.onEvent(...args);
        setInput(key, input);
    })), [descriptor, setInput]);
    const onValues = (0, react_1.useMemo)(() => ((0, descriptors_1.mapDescriptor)(descriptor, ([key, d]) => (value) => {
        setValue(key, value);
    })), [descriptor, setValue]);
    const setSearches = (0, react_1.useMemo)(() => ((0, descriptors_1.mapDescriptor)(descriptor, ([key, d]) => (input) => {
        setSearch(key, input);
    })), [descriptor, setSearch]);
    const isValid = !hasErrors();
    const fields = (0, descriptors_1.mapDescriptor)(descriptor, ([key]) => {
        var _a, _b, _c, _d, _e;
        const result = {
            input: inputs[key],
            value: values[key],
            onEvent: onEvents[key],
            onValue: onValues[key],
            error: errors[key],
            items: (_b = (_a = config[key]) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : [],
            searchInput: (_c = searchInputs[key]) !== null && _c !== void 0 ? _c : '',
            onSearch: setSearches[key],
            modifiers: getModifiers(key, values),
            disabled: (_d = config[key]) === null || _d === void 0 ? void 0 : _d.disabled,
            hidden: (_e = config[key]) === null || _e === void 0 ? void 0 : _e.hidden,
        };
        return result;
    });
    return {
        fields,
        values,
        validateAll,
        isValid,
    };
};
exports.useForm = useForm;
//# sourceMappingURL=useForm.js.map